import { CursorDirectionEnum } from '@/types/generated/graphql';
import { DocumentNode, LazyQueryHookOptions, OperationVariables, useLazyQuery } from '@apollo/client';
import { useIntersectionObserver } from '@shared/hooks/use-intersection-observer';
import { useEffect, useState } from 'react';

type Props<QueryType, VariablesType extends OperationVariables> = {
  schema: DocumentNode;
  variables?: VariablesType;
  responseDataKey: keyof Omit<QueryType, '__typename'>;
  onCompletedCallback?: (data: QueryType) => void;
  options?: LazyQueryHookOptions;
};

const defaultVariables = {
  cursor: null,
  direction: null,
  limit: 15,
  sort: null,
  query: '',
};

export function useLazyCursorPagination<QueryType, VariablesType extends OperationVariables>(
  props: Props<QueryType, VariablesType>
) {
  const { schema, variables = defaultVariables, responseDataKey, onCompletedCallback = () => {}, options = {} } = props;
  const { ref: loadNextRef, isIntersecting: isNextIntersecting } = useIntersectionObserver();
  const [cursorInfo, setCursorInfo] = useState({
    startCursor: '',
    endCursor: '',
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const [fetchQuery, { data, loading, fetchMore }] = useLazyQuery(schema, {
    ...options,
    variables: {
      ...defaultVariables,
      ...variables,
    },
    onCompleted: (data) => {
      onCompletedCallback(data);

      const {
        startCursor = '',
        endCursor = '',
        hasNextPage = false,
        hasPreviousPage = false,
      } = data?.[responseDataKey]?.cursor ?? {};

      setCursorInfo({
        startCursor,
        endCursor,
        hasNextPage,
        hasPreviousPage,
      });
    },
  });

  function fetchNextPage() {
    fetchMore({
      variables: {
        ...variables,
        cursor: cursorInfo.endCursor,
        direction: CursorDirectionEnum.next,
      },
    });
  }

  useEffect(
    function onNextIntersection() {
      const shouldFetchNext = cursorInfo.hasNextPage && isNextIntersecting;
      if (shouldFetchNext) fetchNextPage();
    },
    [isNextIntersecting]
  );

  useEffect(
    function setNewCursorIndex() {
      const {
        startCursor = '',
        endCursor = '',
        hasNextPage = false,
        hasPreviousPage = false,
      } = data?.[responseDataKey]?.cursor ?? {};

      setCursorInfo({
        startCursor,
        endCursor,
        hasNextPage,
        hasPreviousPage,
      });
    },
    [data]
  );

  return {
    fetchQuery: (args: { variables: VariablesType }) => fetchQuery({ ...args }),
    loading,
    loadNextRef,
    hasNextPage: cursorInfo.hasNextPage,
    initialLoading: !data?.data && loading,
    hasPreviousPage: cursorInfo.hasPreviousPage,
    data: data?.[responseDataKey] as QueryType[typeof responseDataKey],
  };
}
